import type { LocationQueryRaw } from '#vue-router';

import { SellingMethod } from '~/apiClient';
import { ROUTE_NAMES } from '~/constants/routes';
import { AlpFilterableState, DslpFilterableState } from '~/types/filters';
import { Locale } from '~i18n/i18nConfig';

type FormatSlpRoute = (
    params: FormatSlpRouteParams,
    query?: Partial<Record<SlpQueryKey, string | number | string[]>>
) => {
    name: string;
    params: { localizedAssetType: string; optionalParams: string[] | undefined };
    query: LocationQueryRaw;
};

type GetSlpLocalizedPath = (locale: Locale) => string;

type ChangeSlpLocale = (locale: Locale) => void;

export interface UseSlpRouteUtilsReturn {
    formatSlpRoute: FormatSlpRoute;
    getSlpLocalizedPath: GetSlpLocalizedPath;
    changeSlpLocale: ChangeSlpLocale;
}

const SLP_SALE_CHANNEL_TO_ROUTE_NAME_MAP = {
    [SellingMethod.Auction]: ROUTE_NAMES.AUCTION_LISTING,
    [SellingMethod.DirectSale]: ROUTE_NAMES.DIRECT_SALE_LISTING,
};

const SLP_SALE_CHANNEL_TO_DEFAULT_QUERY_MAP = {
    [SellingMethod.Auction]: {
        [SLP_QUERY_KEYS.aState]: AlpFilterableState.Running,
    },
    [SellingMethod.DirectSale]: {
        [SLP_QUERY_KEYS.dsState]: DslpFilterableState.Open,
    },
};

export const useSlpRouteUtils = (): UseSlpRouteUtilsReturn => {
    const route = useRoute();
    const { localizedAssetTypes, localizedClasses } = useSlpLocalizedRouteParams();

    const formatSlpRoute: FormatSlpRoute = ({ sellingMethod, assetType, platformClass, brandUrlKey, modelUrlKey }, query = {}) => {
        const getRoute = (optionalParams: string[] | undefined): ReturnType<FormatSlpRoute> => {
            return {
                name: SLP_SALE_CHANNEL_TO_ROUTE_NAME_MAP[sellingMethod],
                params: {
                    localizedAssetType: localizedAssetTypes.value[assetType],
                    optionalParams,
                },
                query: { ...SLP_SALE_CHANNEL_TO_DEFAULT_QUERY_MAP[sellingMethod], ...query },
            };
        };

        if (!assetType && !brandUrlKey && !modelUrlKey) return getRoute(undefined);

        const optionalParams: OptionalParams = {
            localizedClass: platformClass && localizedClasses.value[assetType][platformClass],
            brandUrlKey,
            modelUrlKey,
        };

        return getRoute(formatSlpRouteOptionalParams(optionalParams));
    };

    const getSlpLocalizedPath: GetSlpLocalizedPath = locale => {
        const parsedRouteParams = useSlpParsedRouteParamsState(route);
        const assetType = parsedRouteParams.value.assetType!;
        const localizedAssetType = localizedParams[locale].assetType[assetType];
        const classEnum = parsedRouteParams.value.platformClass ? parsedRouteParams.value.platformClass : undefined;
        const localizedClass = classEnum && localizedParams[locale].class[assetType][classEnum];
        const { brandUrlKey, modelUrlKey } = parsedRouteParams.value;
        const localePath = useLocalePath();

        return localePath(
            {
                params: {
                    localizedAssetType,
                    optionalParams: formatSlpRouteOptionalParams({ localizedClass, brandUrlKey, modelUrlKey }),
                },
                query: route.query,
            },
            locale
        );
    };

    const changeSlpLocale: ChangeSlpLocale = locale => {
        navigateTo(getSlpLocalizedPath(locale));
    };

    return {
        formatSlpRoute,
        getSlpLocalizedPath,
        changeSlpLocale,
    };
};
